import React, { useState, useEffect, useMemo } from 'react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import AuthService from '../../services/authService'
import { FileText, ChevronDown, X } from 'lucide-react'

const LeaveReportsFilters = ({ filters, onFiltersChange }) => {
  const [leaveTypes, setLeaveTypes] = useState([])
  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(false)

  // Get user role and branch
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id

  // Initialize manager's branch filter on mount
  useEffect(() => {
    if (isManager && userBranchId && onFiltersChange) {
      const initialFilters = {
        ...filters,
        branch: userBranchId
      }
      console.log('🔧 Manager detected - Setting initial branch filter:', userBranchId)
      onFiltersChange(initialFilters)
    }
  }, [isManager, userBranchId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load filter options on component mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoading(true)
      try {
        // Import services dynamically
        const { leaveTypeService } = await import('../../services/leaveService')
        const EmployeeService = (await import('../../services/employeeService')).default
        const BranchService = (await import('../../services/branchService')).default

        // Load leave types, employees, and branches in parallel
        const [leaveTypesResponse, employeesResponse, branchesResponse] = await Promise.all([
          leaveTypeService.getAllLeaveTypes(),
          EmployeeService.getAllEmployees(),
          BranchService.getAllBranches()
        ])

        // Set leave types
        if (leaveTypesResponse.success && leaveTypesResponse.data) {
          const types = Array.isArray(leaveTypesResponse.data) 
            ? leaveTypesResponse.data 
            : leaveTypesResponse.data.leaveTypes || []
          setLeaveTypes(types)
        }

        // Set employees
        if (employeesResponse.success && employeesResponse.data) {
          const employeeList = Array.isArray(employeesResponse.data) 
            ? employeesResponse.data 
            : employeesResponse.data.employees || []
          setEmployees(employeeList)
        }

        // Set branches
        if (branchesResponse.success && branchesResponse.data) {
          const branchList = Array.isArray(branchesResponse.data) 
            ? branchesResponse.data 
            : branchesResponse.data.branches || []
          setBranches(branchList.filter(branch => branch.isActive))
        }
      } catch (error) {
        console.error('Error loading filter options:', error)
      } finally {
        setLoading(false)
      }
    }

    loadFilterOptions()
  }, [])

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    }
    onFiltersChange(newFilters)
  }

  const handleClearFilters = () => {
    console.log('🗑️ LeaveFilters: Clear All clicked')
    onFiltersChange({
      leaveType: '',
      employee: '',
      search: '',
      branch: isManager ? userBranchId : '',
      startDate: '',
      endDate: ''
    })
  }

  const hasActiveFilters = useMemo(() => {
    const active = filters?.leaveType || filters?.employee || filters?.search || (!isManager && filters?.branch) || filters?.startDate || filters?.endDate
    return active
  }, [filters, isManager])

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {/* Leave Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Leave Type
          </label>
          <div className="relative">
            <select
              value={filters?.leaveType || ''}
              onChange={(e) => handleFilterChange('leaveType', e.target.value)}
              className="w-full px-3 py-2 pr-8 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm appearance-none"
              disabled={loading}
            >
              <option value="">All Leave Types</option>
              {leaveTypes.map((type) => (
                <option key={type._id || type.id} value={type._id || type.id}>
                  {type.name || type.typeName}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Employee Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Employee
          </label>
          <div className="relative">
            <select
              value={filters?.employee || ''}
              onChange={(e) => handleFilterChange('employee', e.target.value)}
              className="w-full px-3 py-2 pr-8 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm appearance-none"
              disabled={loading}
            >
              <option value="">All Employees</option>
              {employees.map((employee) => (
                <option key={employee._id || employee.id} value={employee._id || employee.id}>
                  {employee.fullName || employee.name || `${employee.firstName} ${employee.lastName}` || employee.username}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Search by ID Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search by ID
          </label>
          <input
            type="text"
            placeholder="Employee ID or Leave ID..."
            value={filters?.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>

        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch
          </label>
          <div className="relative">
            {isManager ? (
              // Manager: Read-only branch display
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium text-sm">
                {branches.find(b => b._id === userBranchId)?.name || 'My Branch'}
              </div>
            ) : (
              // Admin: Branch dropdown
              <>
                <select
                  value={filters?.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 pr-8 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm appearance-none"
                  disabled={loading}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id || branch.id} value={branch._id || branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
        </div>

        {/* Date Range Filters */}
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={filters?.startDate || ''}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>

        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            value={filters?.endDate || ''}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
            disabled={loading}
          />
        </div>
      </div>
    </div>
  )
}

const LeaveReports = () => {
  const columns = [
    { key: 'employeeId', label: 'Employee ID', sortable: true, type: 'text' },
    { key: 'employeeName', label: 'Employee Name', sortable: true, type: 'text' },
    { key: 'leaveType', label: 'Leave Type', sortable: true, type: 'text' },
    { key: 'date', label: 'Date', sortable: true, type: 'date' },
    { key: 'noOfLeaves', label: 'No. of Leaves', sortable: true, type: 'number' },
    { key: 'status', label: 'Status', sortable: true, type: 'text' },
    { key: 'branch', label: 'Branch', sortable: true, type: 'text' }
  ]

  return (
    <BaseReportPage
      title="Leave Reports"
      description="View and analyze employee leave records with comprehensive filtering options"
      reportType="leave-reports"
      columns={columns}
      customFilterComponent={LeaveReportsFilters}
      showExport={true}
      showPrint={true}
    />
  )
}

export default LeaveReports